import React from 'react';
import { Account } from 'types/Account';
import { gql, useQuery } from '@apollo/client';
import { Skeleton, Typography, Paper } from '@mui/material';
import PlaybackCameraPlayer from './PlaybackCameraPlayer';
import { useTranslation } from 'react-i18next';
import WatchLiveButton from './WatchLiveButton';

type CameraPlayback = {
  url: string;
  token: string;
};

const CAMERA_PLAYBACK = gql`
  query GetCameraPlayback(
    $isCloud: Boolean!
    $masterCompanyId: Int!
    $accountId: Int!
    $accountType: Int!
    $doorId: Int
    $email: String
    $isSIMCloud: Boolean
    $isRTSP: Boolean
    $userName: String
    $camName: String!
    $initialDate: String!
    $timezone: Int
  ) {
    getCameraPlayback(
      isCloud: $isCloud
      masterCompanyId: $masterCompanyId
      accountId: $accountId
      accountType: $accountType
      doorId: $doorId
      email: $email
      isSIMCloud: $isSIMCloud
      isRTSP: $isRTSP
      userName: $userName
      camName: $camName
      initialDate: $initialDate
      timezone: $timezone
    ) {
      url
      token
    }
  }
`;

function PlaybackCamera({
  accountSelected,
  camName,
  playbackDate,
}: {
  accountSelected: Account;
  camName: string;
  playbackDate: string;
}) {
  const { t } = useTranslation('cameras');
  const { data, refetch, error } = useQuery<{
    getCameraPlayback: CameraPlayback | null;
  }>(CAMERA_PLAYBACK, {
    fetchPolicy: 'no-cache',
    variables: {
      ...accountSelected,
      accountId: accountSelected?.id,
      camName,
      initialDate: playbackDate,
    },
  });

  React.useEffect(
    function retryOnError() {
      let timeoutId: NodeJS.Timeout | undefined;
      if (error) {
        timeoutId = setTimeout(() => refetch(), 1000);
      }
      return () => clearTimeout(timeoutId);
    },
    [error, refetch],
  );

  const onErrorPlaylist = React.useCallback(() => {
    refetch();
  }, [refetch]);

  if (!data) {
    return <Skeleton sx={{ flex: '1 1 auto', transform: 'none' }} />;
  }

  if (!data.getCameraPlayback) {
    return (
      <Paper
        sx={{
          variant: 'outlined',
          flex: '1 1 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          maxHeight: '662px',
          borderRadius: '5px',
          position: 'relative',
        }}
      >
        <WatchLiveButton />
        <Typography variant="h5">{t('there_is_no_video_at_the_selected_time')}</Typography>
      </Paper>
    );
  }

  const { token, url } = data.getCameraPlayback;

  return <PlaybackCameraPlayer {...{ token, url, camName, onErrorPlaylist, accountSelected }} />;
}

export default PlaybackCamera;
